'use client';

import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { isInZeloolApp } from '@/utils/tools';
import { getOrCreateDeviceId } from './utils/deviceIdStorage';

/** Criteo First-Party Media 身份同步端点。 */
const CRITEO_GUM_ENDPOINT = 'https://gum.criteo.com/fpm/init';

/** Microsoft Advertising 浏览器身份同步像素端点。 */
const BING_ID_SYNC_ENDPOINT = 'https://c.bing.com/c.gif';

/** Bing 会话幂等键；版本号用于未来协议变更时安全升级。 */
const BING_ID_SYNC_SESSION_KEY = 'zl-bing-id-sync-v1';

/** Bing 会话幂等键的最小完成标记。 */
const BING_ID_SYNC_SESSION_VALUE = '1';

/** 当前文档生命周期是否已占用 Criteo GUM 执行权。 */
let didAttemptCriteoGum = false;

/** 当前文档生命周期是否已占用 Bing ID Sync 执行权。 */
let didAttemptBingIdSync = false;

/** Criteo GUM 使用的只读环境配置。 */
export const criteoConfig = Object.freeze({
  partnerId: env.NEXT_PUBLIC_CRITEO_ID?.trim() ?? '',
  firstPartyName: env.NEXT_PUBLIC_CRITEO_FIRST_PARTY_NAME.trim(),
  firstPartyDomain: env.NEXT_PUBLIC_CRITEO_FIRST_PARTY_DOMAIN.trim(),
});

/** Bing Red3 使用独立环境配置，不从 Bing Tag ID 推导。 */
const bingRed3 = env.NEXT_PUBLIC_BING_RED3.trim();

/** 广告身份同步平台名称。 */
type AdvertisingIdentityPlatform = 'criteo' | 'bing' | 'shared';

/** 广告身份同步执行阶段。 */
type AdvertisingIdentityStage = 'config' | 'device-id' | 'request' | 'storage';

/**
 * 将未知异常归一化为不会携带用户身份的固定类型。
 * @param error 捕获到的未知异常。
 * @returns 固定且脱敏的错误类别。
 */
function normalizeErrorType(error: unknown): 'Error' | 'NonError' {
  return error instanceof Error ? 'Error' : 'NonError';
}

/**
 * 记录不包含 Device ID、请求 URL 或环境值的失败摘要。
 * @param platform 失败所属平台。
 * @param stage 失败发生阶段。
 * @param error 可选的原始异常，仅用于归一化类别。
 */
function logIdentitySyncFailure(
  platform: AdvertisingIdentityPlatform,
  stage: AdvertisingIdentityStage,
  error?: unknown,
): void {
  try {
    logger.error('Advertising identity sync failed', {
      platform,
      stage,
      errorType: normalizeErrorType(error),
    });
  } catch {
    // 日志设施异常不能反向阻断页面初始化或第三方同步隔离。
  }
}

/**
 * 校验 Criteo GUM 所需的三个环境配置均非空。
 * @returns 配置是否可安全发送。
 */
function hasValidCriteoConfig(): boolean {
  return Boolean(
    criteoConfig.partnerId && criteoConfig.firstPartyName && criteoConfig.firstPartyDomain,
  );
}

/**
 * 安全读取 Bing 会话完成标记。
 * @returns 当前浏览器会话是否已经完成过同步尝试。
 */
function hasCompletedBingIdSyncInSession(): boolean {
  try {
    return (
      globalThis.sessionStorage?.getItem(BING_ID_SYNC_SESSION_KEY) === BING_ID_SYNC_SESSION_VALUE
    );
  } catch (error) {
    logIdentitySyncFailure('bing', 'storage', error);
    return false;
  }
}

/**
 * 在像素请求前安全占用 Bing 会话幂等标记。
 */
function markBingIdSyncCompletedInSession(): void {
  try {
    globalThis.sessionStorage?.setItem(BING_ID_SYNC_SESSION_KEY, BING_ID_SYNC_SESSION_VALUE);
  } catch (error) {
    logIdentitySyncFailure('bing', 'storage', error);
  }
}

/**
 * 为当前文档占用 Criteo GUM 执行权。
 * @returns 当前调用是否应执行 GUM。
 */
function reserveCriteoGumAttempt(): boolean {
  if (didAttemptCriteoGum) return false;

  didAttemptCriteoGum = true;
  if (hasValidCriteoConfig()) return true;

  logIdentitySyncFailure('criteo', 'config');
  return false;
}

/**
 * 按模块级与会话级双重幂等规则占用 Bing 执行权。
 * @returns 当前调用是否应执行 Bing ID Sync。
 */
function reserveBingIdSyncAttempt(): boolean {
  if (didAttemptBingIdSync) return false;

  didAttemptBingIdSync = true;
  if (hasCompletedBingIdSyncInSession()) return false;
  if (bingRed3) return true;

  logIdentitySyncFailure('bing', 'config');
  return false;
}

/**
 * 非阻塞发送一次 Criteo GUM 请求。
 * @param deviceId 当前页面统一使用的 Snowplow Device ID。
 */
function sendCriteoGum(deviceId: string): void {
  const query = new URLSearchParams({
    first_party_id: deviceId,
    first_party_domain: criteoConfig.firstPartyDomain,
    first_party_name: criteoConfig.firstPartyName,
    p: criteoConfig.partnerId,
  });

  try {
    void fetch(`${CRITEO_GUM_ENDPOINT}?${query.toString()}`, {
      method: 'GET',
      credentials: 'include',
    }).catch(
      /** 隔离第三方网络 Promise 失败，且只记录脱敏摘要。 */
      (error: unknown) => logIdentitySyncFailure('criteo', 'request', error),
    );
  } catch (error) {
    logIdentitySyncFailure('criteo', 'request', error);
  }
}

/**
 * 使用浏览器 Image GET 非阻塞发送一次 Bing ID Sync。
 * @param deviceId 当前页面统一使用的 Snowplow Device ID。
 */
function sendBingIdSync(deviceId: string): void {
  const url = new URL(BING_ID_SYNC_ENDPOINT);
  url.searchParams.set('vid', deviceId);
  url.searchParams.set('Red3', bingRed3);

  // 在设置 src 前占用会话标记，防止并发或失败重入产生重复像素。
  markBingIdSyncCompletedInSession();

  try {
    const pixel = new Image();
    pixel.onerror = () => logIdentitySyncFailure('bing', 'request');
    pixel.src = url.toString();
  } catch (error) {
    logIdentitySyncFailure('bing', 'request', error);
  }
}

/**
 * 在 Web/Wap 客户端初始化时启动 Criteo 与 Bing 身份同步。
 *
 * 该流程与 Consent/CMP 和广告 SDK 开关完全解耦；第三方网络请求不会被等待，
 * 任一路失败也不会向页面或另一条同步链路抛出。
 */
export async function initializeAdvertisingIdentitySync(): Promise<void> {
  if (typeof window === 'undefined' || isInZeloolApp()) return;

  const shouldRunCriteoGum = reserveCriteoGumAttempt();
  const shouldRunBingIdSync = reserveBingIdSyncAttempt();
  if (!shouldRunCriteoGum && !shouldRunBingIdSync) return;

  let deviceId: string;
  try {
    deviceId = (await getOrCreateDeviceId()).trim();
  } catch (error) {
    logIdentitySyncFailure('shared', 'device-id', error);
    return;
  }

  if (!deviceId) {
    logIdentitySyncFailure('shared', 'device-id');
    return;
  }

  if (shouldRunCriteoGum) sendCriteoGum(deviceId);
  if (shouldRunBingIdSync) sendBingIdSync(deviceId);
}
